<template>
  <b-container fluid class="py-4">
    <b-row>
      <b-col>
        <b-card no-body>
          <!-- Toolbar: search, status + entity filters, CSV export -->
          <b-card-header
            class="d-flex align-items-center flex-wrap border-bottom-0"
          >
            <div class="navbar-search form-inline mr-auto">
              <b-input-group class="input-group-alternative input-group-merge">
                <div class="input-group-prepend">
                  <span class="input-group-text"
                    ><i class="fas fa-search"></i
                  ></span>
                </div>
                <b-form-input
                  v-model="search"
                  size="sm"
                  placeholder="Search"
                  type="text"
                ></b-form-input>
              </b-input-group>
            </div>

            <b-form-select
              v-model="statusFilter"
              :options="statusOptions"
              size="sm"
              class="mr-2 mt-2 mt-md-0"
              style="width: 150px"
            ></b-form-select>

            <b-button
              @click="handleExportCsv"
              variant="primary"
              size="sm"
              class="d-flex align-items-center mt-2 mt-md-0"
            >
              <b-icon icon="download" class="mr-1"></b-icon>
              Export CSV
            </b-button>
          </b-card-header>

          <b-table
            :items="pagedItems"
            :fields="tableFields"
            :busy="isBusy"
            :tbody-tr-class="rowClass"
            primary-key="key"
            class="mb-0 export-log-table"
            responsive
            show-empty
            empty-text="No exports found."
          >
            <template #table-busy>
              <div class="text-center py-4">
                <b-spinner class="align-middle mr-2"></b-spinner>
                <span>Loading export log…</span>
              </div>
            </template>

            <!-- Column header: label + (for searchable columns) a search icon
                 that opens a per-column search popover. -->
            <template #head()="{ label, field }">
              <span class="d-inline-flex align-items-center">
                {{ label }}
                <template v-if="field.searchable">
                  <b-button
                    :id="`hsearch-${field.key}`"
                    variant="link"
                    size="sm"
                    class="p-0 ml-1 header-search-btn"
                    :class="{ 'is-active': hasColumnSearch(field.key) }"
                    @click.stop
                  >
                    <b-icon icon="search" font-scale="0.8"></b-icon>
                  </b-button>
                  <b-popover
                    :target="`hsearch-${field.key}`"
                    triggers="click blur"
                    placement="bottom"
                    custom-class="header-search-popover"
                  >
                    <b-form @submit.prevent="applyColumnSearch(field.key)">
                      <!-- Dropdown for status / export type -->
                      <b-form-select
                        v-if="field.searchType === 'select'"
                        v-model="columnSearchDraft[field.key]"
                        :options="selectOptionsFor(field.key)"
                        size="sm"
                        class="mb-2"
                      ></b-form-select>

                      <!-- Date range for next/last run -->
                      <template v-else-if="field.searchType === 'daterange'">
                        <label class="small mb-1 text-muted">From</label>
                        <b-form-input
                          v-model="columnSearchDraft[field.key].from"
                          type="date"
                          size="sm"
                          class="mb-2"
                        ></b-form-input>
                        <label class="small mb-1 text-muted">To</label>
                        <b-form-input
                          v-model="columnSearchDraft[field.key].to"
                          type="date"
                          size="sm"
                          class="mb-2"
                        ></b-form-input>
                      </template>

                      <!-- Free text otherwise -->
                      <b-form-input
                        v-else
                        v-model="columnSearchDraft[field.key]"
                        :placeholder="`Search by ${label}`"
                        size="sm"
                        class="mb-2"
                        autofocus
                      ></b-form-input>

                      <div class="d-flex">
                        <b-button
                          type="submit"
                          size="sm"
                          class="limeiq-apply-btn"
                        >
                          Apply
                        </b-button>
                        <b-button
                          v-if="hasColumnSearch(field.key)"
                          size="sm"
                          variant="link"
                          class="ml-2 text-muted"
                          @click="clearColumnSearch(field.key)"
                        >
                          Clear
                        </b-button>
                      </div>
                    </b-form>
                  </b-popover>
                </template>
              </span>
            </template>

            <!-- Export Name (read-only history — no expand toggle) -->
            <template #cell(name)="{ item }">
              <span class="font-weight-bold">{{ item.name }}</span>
            </template>

            <template #cell(exportType)="{ item }">
              <span v-if="item.exportType">{{ item.exportType }}</span>
              <span v-else class="text-muted">—</span>
            </template>

            <!-- Superuser-only: which company member triggered the export -->
            <template #cell(loggedInUser)="{ item }">
              <span v-if="item.loggedInUser">{{ item.loggedInUser }}</span>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(runDate)="{ item }">
              <span v-if="item.runDate">{{ formatDay(item.runDate) }}</span>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(status)="{ value }">
              <b-badge
                :variant="statusVariant(value)"
                pill
                class="px-3 py-1"
                :class="`status-badge status-${value.toLowerCase()}`"
              >
                {{ value }}
              </b-badge>
            </template>

            <template #cell(recipients)="{ item }">
              <template v-if="recipientFirst(item.recipients)">
                <span class="d-flex align-items-center">
                  {{ recipientFirst(item.recipients) }}
                  <template v-if="recipientExtraCount(item.recipients)">
                    <small
                      v-if="expandedRecipients !== item.key"
                      class="recipient-more ml-2"
                      >+{{ recipientExtraCount(item.recipients) }}</small
                    >
                    <a
                      @click.stop.prevent="toggleRecipients(item.key)"
                      class="px-2 recipient-toggle"
                    >
                      <b-icon
                        :icon="
                          expandedRecipients === item.key
                            ? 'caret-down-fill'
                            : 'caret-right-fill'
                        "
                      ></b-icon>
                    </a>
                  </template>
                </span>
                <b-collapse :visible="expandedRecipients === item.key">
                  <span
                    v-for="(email, idx) in recipientRest(item.recipients)"
                    :key="`rcpt-${item.key}-${idx}`"
                    class="d-block"
                    >{{ email }}</span
                  >
                </b-collapse>
              </template>
              <span v-else class="text-muted">—</span>
            </template>

            <!-- Download only — the log is read-only (feedback #6). -->
            <template #cell(download)="{ item }">
              <a
                v-if="oneTimeLink(item)"
                v-b-tooltip.hover
                title="Download"
                :href="oneTimeLink(item)"
                download
                class="text-primary"
              >
                <b-icon icon="download"></b-icon>
              </a>
              <span v-else class="text-muted">—</span>
            </template>

          </b-table>

          <b-card-footer
            class="d-flex align-items-center justify-content-center justify-content-sm-between flex-wrap"
          >
            <div class="d-flex align-items-center text-sm">
              Show
              <b-form-select
                v-model="perPage"
                :options="perPageOptions"
                size="sm"
                class="mx-2"
                style="width: auto"
              ></b-form-select>
              entries
              <span class="ml-2 text-muted">({{ totalRows }} total)</span>
            </div>

            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              class="m-0"
              hide-ellipsis
            ></b-pagination>
          </b-card-footer>
        </b-card>
      </b-col>
    </b-row>

  </b-container>
</template>

<script>
import Vue from 'vue'
import {
  defineComponent,
  ref,
  reactive,
  computed,
  watch,
  onMounted
} from '@vue/composition-api'
import { useRoute } from 'vue2-helpers/vue-router'
import moment from 'moment'

import ExportLogService, { STATUS } from '@services/exportLogService'
import useCurrentUser from '@composables/useCurrentUser'

const STATUS_VARIANTS = {
  [STATUS.ACTIVE]: 'success',
  [STATUS.PAUSED]: 'secondary',
  [STATUS.CANCELLED]: 'danger',
  [STATUS.DELIVERED]: 'success',
  [STATUS.PENDING]: 'warning',
  [STATUS.FAILED]: 'danger'
}

export default defineComponent({
  name: 'ExportLog',
  setup() {
    const route = useRoute()
    const { isSuperUser } = useCurrentUser()

    const isBusy = ref(true)
    const items = ref([])

    const search = ref('')
    const statusFilter = ref(null)

    const currentPage = ref(1)
    const perPage = ref(25)
    const perPageOptions = [10, 25, 50, 100]

    // The "Logged-in User" column is only available to superusers.
    const tableFields = computed(() => {
      // Export Log is a read-only chronological history of actual exports
      // (feedback #6): no scheduled exports, no Frequency/Next Run, no actions.
      const fields = [
        { key: 'name', label: 'Export Name', sortable: true, searchable: true, searchType: 'text', thStyle: { width: '200px' } },
        { key: 'exportType', label: 'Export Type', sortable: true, searchable: true, searchType: 'select', thStyle: { width: '150px' } },
        { key: 'runDate', label: 'Run Date', sortable: true, searchable: true, searchType: 'daterange', thStyle: { width: '160px' } },
        { key: 'status', label: 'Status', sortable: true, searchable: true, searchType: 'select', thStyle: { width: '100px' } },
        { key: 'recipients', label: 'Recipients', searchable: true, searchType: 'text', thStyle: { width: '200px' } },
        { key: 'download', label: 'Download', thStyle: { width: '90px' } }
      ]
      if (isSuperUser.value) {
        // Insert "Logged-in User" just after Recipients.
        const recipientsIdx = fields.findIndex((f) => f.key === 'recipients')
        const insertAt = recipientsIdx >= 0 ? recipientsIdx + 1 : fields.length
        fields.splice(insertAt, 0, {
          key: 'loggedInUser',
          label: 'Logged-in User',
          sortable: true,
          searchable: true,
          searchType: 'text',
          thStyle: { width: '160px' }
        })
      }
      return fields
    })

    // The Export Log is a chronological history of actual export RUNS, so only
    // run statuses apply. Schedule-only statuses (Active/Paused/Cancelled) are
    // excluded from the status filters here.
    const RUN_STATUSES = [STATUS.DELIVERED, STATUS.PENDING, STATUS.FAILED]

    // Options for the dropdown-style column searches.
    const statusSearchOptions = [
      { value: '', text: 'All' },
      ...RUN_STATUSES.map((s) => ({ value: s, text: s }))
    ]
    const exportTypeOptions = [
      { value: '', text: 'All' },
      { value: 'Market Split', text: 'Market Split' },
      { value: 'Custom Split', text: 'Custom Split' },
      { value: 'Full Data Export', text: 'Full Data Export' }
    ]

    // Pick the right option list for a select-type column search.
    const selectOptionsFor = (key) => {
      if (key === 'exportType') return exportTypeOptions
      return statusSearchOptions
    }

    // Applied per-column search terms (filter uses these). Text/select hold a
    // string; date-range columns hold { from, to }. Pre-initialised so Vue 2
    // reactivity tracks each searchable column.
    const columnSearch = reactive({
      name: '',
      exportType: '',
      loggedInUser: '',
      status: '',
      recipients: '',
      runDate: { from: '', to: '' }
    })

    // Draft values bound to the popover controls; committed on Apply.
    const columnSearchDraft = reactive({
      name: '',
      exportType: '',
      loggedInUser: '',
      status: '',
      recipients: '',
      runDate: { from: '', to: '' }
    })

    const DATE_COLUMNS = ['runDate']

    const applyColumnSearch = (key) => {
      if (DATE_COLUMNS.includes(key)) {
        columnSearch[key] = {
          from: columnSearchDraft[key].from || '',
          to: columnSearchDraft[key].to || ''
        }
      } else {
        columnSearch[key] = (columnSearchDraft[key] || '').trim()
      }
    }

    const clearColumnSearch = (key) => {
      if (DATE_COLUMNS.includes(key)) {
        columnSearch[key] = { from: '', to: '' }
        columnSearchDraft[key] = { from: '', to: '' }
      } else {
        columnSearch[key] = ''
        columnSearchDraft[key] = ''
      }
    }

    // True when a column has an active search applied.
    const hasColumnSearch = (key) => {
      const v = columnSearch[key]
      if (DATE_COLUMNS.includes(key)) return !!(v && (v.from || v.to))
      return !!v
    }

    // Text/select column → searchable string.
    const columnValue = (item, key) => {
      switch (key) {
        case 'name':
          return item.name || ''
        case 'exportType':
          return item.exportType || ''
        case 'loggedInUser':
          return item.loggedInUser || ''
        case 'status':
          return item.status || ''
        case 'recipients':
          return Array.isArray(item.recipients)
            ? item.recipients.join(' ')
            : item.recipients || ''
        default:
          return ''
      }
    }

    // Does a row's date column fall within the applied { from, to } range?
    const dateInRange = (item, key) => {
      const range = columnSearch[key]
      const value = item[key]
      if (!value) return false
      const d = moment(value)
      if (range.from && d.isBefore(moment(range.from), 'day')) return false
      if (range.to && d.isAfter(moment(range.to), 'day')) return false
      return true
    }

    const statusOptions = computed(() => [
      { value: null, text: 'All Statuses' },
      ...RUN_STATUSES.map((s) => ({ value: s, text: s }))
    ])

    const filteredItems = computed(() => {
      const term = search.value.trim().toLowerCase()
      const activeKeys = Object.keys(columnSearch).filter(hasColumnSearch)
      return items.value.filter((item) => {
        if (statusFilter.value && item.status !== statusFilter.value)
          return false
        if (term && !(item.name || '').toLowerCase().includes(term))
          return false
        // Per-column header searches (all must match).
        for (const key of activeKeys) {
          if (DATE_COLUMNS.includes(key)) {
            if (!dateInRange(item, key)) return false
          } else {
            const needle = String(columnSearch[key]).trim().toLowerCase()
            // select columns (status/export type) match exactly;
            // text columns match by "contains".
            if (
              key === 'status' ||
              key === 'exportType'
            ) {
              if (columnValue(item, key).toLowerCase() !== needle) return false
            } else if (!columnValue(item, key).toLowerCase().includes(needle)) {
              return false
            }
          }
        }
        return true
      })
    })

    const totalRows = computed(() => filteredItems.value.length)

    const pagedItems = computed(() => {
      const start = (currentPage.value - 1) * perPage.value
      return filteredItems.value.slice(start, start + perPage.value)
    })

    // Reset to the first page whenever the result set changes, so we never
    // land on a now-empty page after filtering/searching.
    watch(
      [search, statusFilter, perPage, () => JSON.stringify(columnSearch)],
      () => {
        currentPage.value = 1
      }
    )

    // Helpers
    const formatDay = (date) =>
      date ? moment(date).format('MMM D, YYYY') : '—'

    const formatTime = (date) => (date ? moment(date).format('hh:mm A') : '—')

    const statusVariant = (status) => STATUS_VARIANTS[status] || 'secondary'

    // Normalize a recipients value (array | string | null) to a clean array.
    const recipientList = (recipients) => {
      if (!recipients) return []
      if (Array.isArray(recipients)) return recipients.filter(Boolean)
      return String(recipients)
        .split(/[;,]/)
        .map((s) => s.trim())
        .filter(Boolean)
    }

    // Compact cell display: first recipient only.
    const recipientFirst = (recipients) => recipientList(recipients)[0] || ''

    // How many recipients beyond the first (for the "+N" badge).
    const recipientExtraCount = (recipients) =>
      Math.max(0, recipientList(recipients).length - 1)

    // Recipients beyond the first (revealed when the row's caret is expanded).
    const recipientRest = (recipients) => recipientList(recipients).slice(1)

    // Tracks which row's recipient list is expanded inline (by row key/id).
    const expandedRecipients = ref(null)
    const toggleRecipients = (rowKey) => {
      expandedRecipients.value =
        expandedRecipients.value === rowKey ? null : rowKey
    }

    // Download link for a one-time export row (only when the file exists).
    const oneTimeLink = (item) => {
      // Flat export rows carry the link/status directly (no nested runs).
      if (!item || item.isPending || item.isFailed || !item.link) return null
      return item.link
    }

    const handleExportCsv = () => {
      ExportLogService.toCsv(filteredItems.value, {
        includeUser: isSuperUser.value
      })
    }

    // ---- Scheduled export actions ----------------------------------------

    // When arriving from an export-ready email, the URL carries
    // ?download_report=<ExportRequest id>. Find that run, expand its row, and
    // start the file download. (The legacy page only highlighted the row; the
    // download here is the one new behaviour.)
    const triggerDownloadFromQuery = () => {
      const id = route.query && route.query.download_report
      if (!id) return

      // The log is a flat list of export rows (no nested runs), so match the
      // requested export directly on the row id.
      const targetRun = items.value.find((row) => {
        // eslint-disable-next-line eqeqeq
        return row.id == id
      })

      if (!targetRun) {
        Vue.prototype.$toasted.show(
          'That export could not be found. It may have been removed.',
          { type: 'error' }
        )
        return
      }

      if (targetRun.link && !targetRun.isPending && !targetRun.isFailed) {
        const link = document.createElement('a')
        link.href = targetRun.link
        link.setAttribute('download', '')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else if (targetRun.isPending) {
        Vue.prototype.$toasted.show(
          'This export is still being generated. Please try again shortly.',
          { type: 'info' }
        )
      } else if (targetRun.isFailed) {
        Vue.prototype.$toasted.show(
          'This export failed and has no file to download.',
          { type: 'error' }
        )
      }
    }

    const fetchItems = async () => {
      isBusy.value = true
      try {
        items.value = await ExportLogService.fetch()
      } catch (err) {
        items.value = []
        return Promise.reject(err)
      } finally {
        isBusy.value = false
      }
    }

    const highlightedKey = ref(null)

    const applyHighlightFromQuery = async () => {
      const key = route.query && route.query.highlight
      if (!key) return

      const targetIndex = items.value.findIndex((item) => item.key === key)
      if (targetIndex < 0) return

      currentPage.value = Math.floor(targetIndex / perPage.value) + 1
      highlightedKey.value = key

      await Vue.nextTick()
      const el = document.querySelector('.export-log-highlight')
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })

      setTimeout(() => { highlightedKey.value = null }, 4000)
    }

    const rowClass = (item) => {
      if (!highlightedKey.value || !item) return ''
      return item.key === highlightedKey.value ? 'export-log-highlight' : ''
    }

    onMounted(async () => {
      await fetchItems()
      triggerDownloadFromQuery()
      applyHighlightFromQuery()
    })

    return {
      isSuperUser,
      isBusy,
      search,
      columnSearch,
      columnSearchDraft,
      applyColumnSearch,
      clearColumnSearch,
      hasColumnSearch,
      selectOptionsFor,
      tableFields,
      statusOptions,
      statusFilter,
      filteredItems,
      pagedItems,
      currentPage,
      perPage,
      perPageOptions,
      totalRows,
      formatDay,
      formatTime,
      statusVariant,
      recipientFirst,
      recipientExtraCount,
      recipientRest,
      expandedRecipients,
      toggleRecipients,
      oneTimeLink,
      handleExportCsv,
      rowClass
    }
  }
})
</script>

<style scoped>
>>> .table td {
  vertical-align: middle;
}


>>> .export-log-table thead th {
  background-color: #fff !important;
  color: #1a3a4a !important;
  border-top: 1px solid #e9ecef !important;
  border-bottom: 2px solid #212529 !important;
  vertical-align: middle;
  font-size: 0.875rem;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: uppercase;
  padding: 0.5rem 1rem;
  white-space: nowrap;
}

/* Match the body cells' padding to the tighter header. */
>>> .export-log-table tbody td {
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
}

/* Dark-teal sort-direction arrows on the light header. b-table draws them
   as a background-image SVG; recolour to the brand teal for contrast. */
>>> .export-log-table thead th[aria-sort] {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%231a3a4a' d='M3.5 5.5 8 1l4.5 4.5H3.5zM3.5 10.5 8 15l4.5-4.5H3.5z' opacity='0.5'/%3e%3c/svg%3e") !important;
  background-repeat: no-repeat !important;
  background-position: right 0.3rem center !important;
  background-size: 0.7rem 0.7rem !important;
  padding-right: 1.2rem !important;
}
>>> .export-log-table thead th[aria-sort='ascending'] {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%231a3a4a' d='M3.5 5.5 8 1l4.5 4.5H3.5z'/%3e%3cpath fill='%231a3a4a' d='M3.5 10.5 8 15l4.5-4.5H3.5z' opacity='0.4'/%3e%3c/svg%3e") !important;
}
>>> .export-log-table thead th[aria-sort='descending'] {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%231a3a4a' d='M3.5 5.5 8 1l4.5 4.5H3.5z' opacity='0.4'/%3e%3cpath fill='%231a3a4a' d='M3.5 10.5 8 15l4.5-4.5H3.5z'/%3e%3c/svg%3e") !important;
}

/* Header per-column search icon — dark-teal to sit on the light header. */
.header-search-btn {
  color: #1a3a4a !important;
  line-height: 1;
}
.header-search-btn:hover {
  color: #0f2530 !important;
}
.header-search-btn.is-active {
  color: #1b7a4b !important; /* highlight when a search is applied */
}

/* Apply button in the header search popover — brand metal-green. */
>>> .limeiq-apply-btn {
  background-color: #1a3a4a !important;
  border-color: #1a3a4a !important;
  color: #fff !important;
}
>>> .header-search-popover {
  min-width: 220px;
}

/* Highlight the row of the export just conducted when arriving from the wizard. */
>>> .export-log-highlight td {
  background-color: #e8f4f1 !important;
  transition: background-color 0.5s ease;
}

/* Let columns size to content rather than stretching to fill the page. */
>>> .export-log-table {
  width: auto;
  min-width: 100%;
}

/* Fixed-width caret slot so names align whether or not a caret is shown. */
.name-caret {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  flex: 0 0 1.25rem;
  margin-right: 0.5rem;
}

/* Expand caret in the brand metal-green, matching the header. */
.expand-toggle {
  color: #1a3a4a !important;
  line-height: 1;
}

/* Soft, pale status pills to match the design (esp. Pending = yellow). */
>>> .status-badge.status-pending {
  background-color: #fcf3cf !important;
  color: #8a6d00 !important;
}
>>> .status-badge.status-delivered {
  background-color: #d6f5e3 !important;
  color: #1b7a4b !important;
}
>>> .status-badge.status-active {
  background-color: #1a3a4a !important;
  color: #fff !important;
}
>>> .status-badge.status-failed {
  background-color: #fadbd8 !important;
  color: #a93226 !important;
}
>>> .status-badge.status-paused {
  background-color: #e9ecef !important;
  color: #5a6268 !important;
}
>>> .status-badge.status-cancelled {
  background-color: #fadbd8 !important;
  color: #a93226 !important;
}

/* Run history sub-table: light-grey header with dark text. */
.run-history {
  background-color: #f4f6f7;
}

>>> .run-history-table thead th {
  background-color: #eef1f2 !important;
  color: #1a3a4a !important;
  border-color: #e0e4e6 !important;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: uppercase;
  padding: 0.4rem 0.75rem;
  white-space: nowrap;
}

>>> .run-history-table tbody td {
  background-color: #f4f6f7;
  font-size: 0.82rem;
  padding: 0.35rem 0.75rem;
  border-color: #e6e9eb;
}

.action-links .btn {
  font-size: 0.8rem;
  white-space: nowrap;
}

.action-links .action-sep {
  margin: 0 6px;
  color: #d0d5dd;
  font-size: 0.8rem;
}

/* Action links (Cancel / Activate / Resume) in the brand dark-green. */
.action-brand {
  color: #1a3a4a !important;
}
.action-brand:hover {
  color: #0f2530 !important;
}

/* "+N" badge for extra recipients beyond the first (caret expands the list). */
.recipient-more {
  background-color: #eef1f2;
  color: #1a3a4a;
  font-weight: 600;
  border-radius: 10px;
  padding: 0 6px;
  line-height: 1.4;
  cursor: default;
}

/* Caret that expands/collapses the extra recipients inline. */
.recipient-toggle {
  color: #1a3a4a !important;
  cursor: pointer;
  line-height: 1;
}
.recipient-toggle:hover {
  color: #0f2530 !important;
}

</style>
